const SITE_URL = "https://www.airportjourneyplanner.com";

const BASE_ROUTES = [
  {
    key: "nottingham-to-heathrow",
    path: "/plan/nottingham-to-heathrow-airport",
    airportKey: "heathrow",
    airportCode: "LHR",
    airportName: "Heathrow Airport",
    direction: "outbound",
    city: "Nottingham",
    citySlug: "nottingham",
    postcodeHint: "NG1",
    title: "How to Get from Nottingham to Heathrow Airport | Live Journey Planner",
    description: "Plan Nottingham to Heathrow Airport. Compare train, taxi, minibus and self-drive costs, journey times and when to leave for your flight.",
    heading: "How to get from Nottingham to Heathrow Airport",
    intro: "Enter your Nottingham address and flight to compare rail, taxi, minibus and self-drive, with flight-aware timing and current Heathrow journey conditions.",
    badge: "Live Nottingham to Heathrow planning",
    addressPlaceholder: "Enter your Nottingham address or postcode",
    addressLabel: "Travelling from Nottingham",
    reversePath: "/plan/heathrow-airport-to-nottingham",
    reverseLabel: "Plan Heathrow Airport to Nottingham",
    routeContext: {
      roads: ["M1", "M25"],
      typicalRoadTime: "Around 2 hr 30 min to 3 hr 30 min before disruption, loading and terminal access time.",
      railSummary: "Most rail journeys connect Nottingham with London St Pancras, followed by a transfer to the Elizabeth line, Heathrow Express or Piccadilly line for the airport.",
      stations: ["Nottingham", "London St Pancras", "London Paddington", "Heathrow terminals"],
      whenToLeave: "Your generated plan works backwards from the selected flight time and includes the airport arrival buffer. Recheck the live plan on the day of travel.",
    },
    faqs: [
      {
        question: "How long does it take to get from Nottingham to Heathrow Airport?",
        answer: "Driving commonly takes around 2 hours 30 minutes to 3 hours 30 minutes before traffic, stops and terminal access. Rail usually requires at least one London transfer, so the exact flight-timed plan is important.",
      },
      {
        question: "Is there a direct train from Nottingham to Heathrow Airport?",
        answer: "There is normally no single direct train. A common route is Nottingham to London St Pancras, then transfer across London for the Elizabeth line, Heathrow Express or Piccadilly line.",
      },
      {
        question: "When should I leave Nottingham for a Heathrow flight?",
        answer: "Enter the flight number or departure time in the planner. It calculates a suggested leave-home time using the journey estimate and an airport arrival buffer.",
      },
    ],
  },
  {
    key: "heathrow-to-nottingham",
    path: "/plan/heathrow-airport-to-nottingham",
    airportKey: "heathrow",
    airportCode: "LHR",
    airportName: "Heathrow Airport",
    direction: "return",
    city: "Nottingham",
    citySlug: "nottingham",
    postcodeHint: "NG1",
    title: "How to Get from Heathrow Airport to Nottingham | Live Journey Planner",
    description: "Plan Heathrow Airport to Nottingham after landing. Compare train, taxi and minibus costs, journey times and airport exit timing for your flight.",
    heading: "How to get from Heathrow Airport to Nottingham",
    intro: "Enter your Nottingham destination and arriving flight to compare rail, taxi and minibus options from Heathrow, with terminal-aware timing and current journey conditions.",
    badge: "Live Heathrow to Nottingham planning",
    addressPlaceholder: "Enter your Nottingham destination",
    addressLabel: "Travelling to Nottingham",
    reversePath: "/plan/nottingham-to-heathrow-airport",
    reverseLabel: "Plan Nottingham to Heathrow Airport",
    routeContext: {
      roads: ["M25", "M1"],
      typicalRoadTime: "Around 2 hr 30 min to 3 hr 30 min after leaving the terminal, before disruption and stops.",
      railSummary: "Rail journeys commonly use the Elizabeth line or Heathrow Express into London, then continue from London St Pancras to Nottingham.",
      stations: ["Heathrow terminals", "London Paddington", "London St Pancras", "Nottingham"],
      whenToLeave: "The return planner uses the selected flight arrival and allows for leaving the terminal before comparing the onward journey options.",
    },
    faqs: [
      {
        question: "How long does it take to get from Heathrow Airport to Nottingham?",
        answer: "Driving commonly takes around 2 hours 30 minutes to 3 hours 30 minutes after leaving the terminal. Rail journeys include a transfer across London before the train to Nottingham.",
      },
      {
        question: "Is there a direct train from Heathrow Airport to Nottingham?",
        answer: "There is normally no single direct train. Travel from Heathrow into central London, transfer to London St Pancras and continue to Nottingham.",
      },
      {
        question: "Can the planner use my Heathrow arrival flight?",
        answer: "Yes. Select an arriving flight or enter its arrival time manually. The planner uses that time when preparing the onward comparison from Heathrow.",
      },
    ],
  },
];

const M1_CORRIDOR_CITIES = [
  {
    key: "watford",
    city: "Watford",
    postcodeHint: "WD17",
    roadsToAirport: ["M1", "M25"],
    roadsFromAirport: ["M25", "M1"],
    roadTime: "Around 30 minutes to 1 hour before congestion, pickup time and terminal access.",
    railOut: "Rail options commonly start at Watford Junction and travel via London Euston, followed by a transfer for the Elizabeth line, Heathrow Express or Piccadilly line.",
    railBack: "Rail options commonly use the Elizabeth line, Heathrow Express or Piccadilly line into London, then continue from London Euston to Watford Junction.",
    stationsOut: ["Watford Junction", "London Euston", "Elizabeth line or Heathrow Express", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Elizabeth line or Heathrow Express", "London Euston", "Watford Junction"],
    railNote: "The Green Line 724 coach can also be relevant for some Watford journeys, but availability and journey time should be checked for the travel date.",
  },
  {
    key: "st-albans",
    city: "St Albans",
    postcodeHint: "AL1",
    roadsToAirport: ["M1", "M25"],
    roadsFromAirport: ["M25", "M1"],
    roadTime: "Around 40 minutes to 1 hour 10 minutes before congestion, loading and terminal access.",
    railOut: "A common rail route uses St Albans City to London St Pancras or Farringdon, then the Elizabeth line or another Heathrow connection.",
    railBack: "A common return route uses the Elizabeth line from Heathrow to Farringdon, followed by a Thameslink service to St Albans City.",
    stationsOut: ["St Albans City", "Farringdon", "Elizabeth line", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Elizabeth line", "Farringdon", "St Albans City"],
  },
  {
    key: "hemel-hempstead",
    city: "Hemel Hempstead",
    postcodeHint: "HP1",
    roadsToAirport: ["M1", "M25"],
    roadsFromAirport: ["M25", "M1"],
    roadTime: "Around 45 minutes to 1 hour 15 minutes before congestion, loading and terminal access.",
    railOut: "Rail journeys commonly use Hemel Hempstead to London Euston, followed by a transfer across London for an Elizabeth line, Heathrow Express or Underground connection.",
    railBack: "Rail journeys commonly travel from Heathrow into central London, then continue from London Euston to Hemel Hempstead.",
    stationsOut: ["Hemel Hempstead", "London Euston", "Elizabeth line or Heathrow Express", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Elizabeth line or Heathrow Express", "London Euston", "Hemel Hempstead"],
  },
  {
    key: "luton",
    city: "Luton",
    postcodeHint: "LU1",
    roadsToAirport: ["M1", "M25"],
    roadsFromAirport: ["M25", "M1"],
    roadTime: "Around 50 minutes to 1 hour 30 minutes from Luton town before congestion, loading and Heathrow terminal access.",
    railOut: "From Luton town, rail journeys commonly use Luton station to London St Pancras or Farringdon, then change for the Elizabeth line to Heathrow.",
    railBack: "For Luton town—not Luton Airport—a common return uses the Elizabeth line to Farringdon and a Thameslink service to Luton station.",
    stationsOut: ["Luton station", "Farringdon", "Elizabeth line", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Elizabeth line", "Farringdon", "Luton station"],
    railNote: "These pages plan between Luton town and Heathrow Airport; they are not pages for travel between Heathrow and Luton Airport.",
  },
  {
    key: "milton-keynes",
    city: "Milton Keynes",
    postcodeHint: "MK9",
    roadsToAirport: ["M1", "M25"],
    roadsFromAirport: ["M25", "M1"],
    roadTime: "Around 1 hour to 1 hour 40 minutes before congestion, stops and Heathrow terminal access.",
    railOut: "Rail journeys commonly use Milton Keynes Central to London Euston, then transfer across London for the Elizabeth line, Heathrow Express or Piccadilly line.",
    railBack: "Rail journeys commonly travel from Heathrow into central London, then continue from London Euston to Milton Keynes Central.",
    stationsOut: ["Milton Keynes Central", "London Euston", "Elizabeth line or Heathrow Express", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Elizabeth line or Heathrow Express", "London Euston", "Milton Keynes Central"],
  },
  {
    key: "northampton",
    city: "Northampton",
    postcodeHint: "NN1",
    roadsToAirport: ["M1", "M25"],
    roadsFromAirport: ["M25", "M1"],
    roadTime: "Around 1 hour 20 minutes to 2 hours before congestion, stops and terminal access.",
    railOut: "Rail journeys commonly use Northampton to London Euston, followed by a transfer across London for the Elizabeth line, Heathrow Express or Underground.",
    railBack: "Rail journeys commonly travel from Heathrow into central London, then continue from London Euston to Northampton.",
    stationsOut: ["Northampton", "London Euston", "Elizabeth line or Heathrow Express", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Elizabeth line or Heathrow Express", "London Euston", "Northampton"],
  },
  {
    key: "rugby",
    city: "Rugby",
    postcodeHint: "CV21",
    roadsToAirport: ["M45", "M1", "M25"],
    roadsFromAirport: ["M25", "M1", "M45"],
    roadTime: "Around 1 hour 40 minutes to 2 hours 30 minutes before congestion, stops and terminal access.",
    railOut: "Rail journeys commonly use Rugby to London Euston, then require a transfer across London for the Elizabeth line, Heathrow Express or Underground.",
    railBack: "Rail journeys commonly travel from Heathrow into central London, then continue from London Euston to Rugby.",
    stationsOut: ["Rugby", "London Euston", "Elizabeth line or Heathrow Express", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Elizabeth line or Heathrow Express", "London Euston", "Rugby"],
  },
  {
    key: "leicester",
    city: "Leicester",
    postcodeHint: "LE1",
    roadsToAirport: ["M1", "M25"],
    roadsFromAirport: ["M25", "M1"],
    roadTime: "Around 1 hour 45 minutes to 2 hours 45 minutes before congestion, stops and terminal access.",
    railOut: "Rail journeys commonly use Leicester to London St Pancras, followed by a transfer to Farringdon, Paddington or the Piccadilly line for Heathrow.",
    railBack: "Rail journeys commonly travel from Heathrow into London, transfer to London St Pancras and continue to Leicester.",
    stationsOut: ["Leicester", "London St Pancras", "Farringdon or Paddington", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Farringdon or Paddington", "London St Pancras", "Leicester"],
  },
  {
    key: "loughborough",
    city: "Loughborough",
    postcodeHint: "LE11",
    roadsToAirport: ["M1", "M25"],
    roadsFromAirport: ["M25", "M1"],
    roadTime: "Around 2 hours to 3 hours before congestion, stops and Heathrow terminal access.",
    railOut: "Rail journeys commonly use Loughborough to London St Pancras, followed by a London transfer for the Elizabeth line, Heathrow Express or Piccadilly line.",
    railBack: "Rail journeys commonly travel from Heathrow into London, transfer to London St Pancras and continue to Loughborough.",
    stationsOut: ["Loughborough", "London St Pancras", "Farringdon or Paddington", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Farringdon or Paddington", "London St Pancras", "Loughborough"],
  },
  {
    key: "derby",
    city: "Derby",
    postcodeHint: "DE1",
    roadsToAirport: ["A50", "M1", "M25"],
    roadsFromAirport: ["M25", "M1", "A50"],
    roadTime: "Around 2 hours 15 minutes to 3 hours 15 minutes before congestion, stops and terminal access.",
    railOut: "Rail journeys commonly use Derby to London St Pancras, followed by a London transfer for the Elizabeth line, Heathrow Express or Piccadilly line.",
    railBack: "Rail journeys commonly travel from Heathrow into London, transfer to London St Pancras and continue to Derby.",
    stationsOut: ["Derby", "London St Pancras", "Farringdon or Paddington", "Heathrow terminals"],
    stationsBack: ["Heathrow terminals", "Farringdon or Paddington", "London St Pancras", "Derby"],
  },
];

function corridorRoutePages(city) {
  const outboundPath = `/plan/${city.key}-to-heathrow-airport`;
  const returnPath = `/plan/heathrow-airport-to-${city.key}`;
  const note = city.railNote ? ` ${city.railNote}` : "";
  return [
    {
      key: `${city.key}-to-heathrow`, path: outboundPath, airportKey: "heathrow", airportCode: "LHR", airportName: "Heathrow Airport", direction: "outbound", city: city.city, citySlug: city.key, postcodeHint: city.postcodeHint,
      title: `How to Get from ${city.city} to Heathrow Airport | Live Journey Planner`,
      description: `Plan ${city.city} to Heathrow Airport. Compare rail, taxi, minibus and self-drive times, fares and when to leave for your flight.`,
      heading: `How to get from ${city.city} to Heathrow Airport`,
      intro: `Enter your ${city.city} address and flight to compare rail, taxi, minibus and self-drive, with flight-aware timing and current Heathrow journey conditions.`,
      badge: `Live ${city.city} to Heathrow planning`, addressPlaceholder: `Enter your ${city.city} address or postcode`, addressLabel: `Travelling from ${city.city}`, reversePath: returnPath, reverseLabel: `Plan Heathrow Airport to ${city.city}`,
      routeContext: { roads: city.roadsToAirport, typicalRoadTime: `A typical road journey is ${city.roadTime} Live routing may use a different corridor when conditions change.`, railSummary: `${city.railOut}${note}`, stations: city.stationsOut, whenToLeave: "The generated plan works backwards from the selected flight departure time, journey duration and airport arrival buffer. Recheck live conditions on the day of travel." },
      faqs: [
        { question: `How long does it take to get from ${city.city} to Heathrow Airport?`, answer: `A typical road journey is ${city.roadTime} The live planner calculates a journey-specific duration from the exact address and current route conditions.` },
        { question: `How do I travel by train from ${city.city} to Heathrow Airport?`, answer: `${city.railOut}${note}` },
        { question: `When should I leave ${city.city} for a Heathrow flight?`, answer: "Enter the flight number or departure time. The planner works backwards using the journey estimate and airport arrival buffer to suggest when to leave home." },
      ],
    },
    {
      key: `heathrow-to-${city.key}`, path: returnPath, airportKey: "heathrow", airportCode: "LHR", airportName: "Heathrow Airport", direction: "return", city: city.city, citySlug: city.key, postcodeHint: city.postcodeHint,
      title: `How to Get from Heathrow Airport to ${city.city} | Live Journey Planner`,
      description: `Plan Heathrow Airport to ${city.city} after landing. Compare rail, taxi and minibus times, fares and terminal-exit timing for your flight.`,
      heading: `How to get from Heathrow Airport to ${city.city}`,
      intro: `Enter your ${city.city} destination and arriving flight to compare rail, taxi and minibus options from Heathrow, with terminal-aware timing and current journey conditions.`,
      badge: `Live Heathrow to ${city.city} planning`, addressPlaceholder: `Enter your ${city.city} destination`, addressLabel: `Travelling to ${city.city}`, reversePath: outboundPath, reverseLabel: `Plan ${city.city} to Heathrow Airport`,
      routeContext: { roads: city.roadsFromAirport, typicalRoadTime: `A typical road journey is ${city.roadTime} Live routing may use a different corridor when conditions change.`, railSummary: `${city.railBack}${note}`, stations: city.stationsBack, whenToLeave: "The return plan uses the selected flight arrival time and allows for leaving the terminal before comparing onward journey options." },
      faqs: [
        { question: `How long does it take to get from Heathrow Airport to ${city.city}?`, answer: `A typical road journey is ${city.roadTime} The live planner calculates a journey-specific duration after the terminal-exit allowance.` },
        { question: `How do I travel by train from Heathrow Airport to ${city.city}?`, answer: `${city.railBack}${note}` },
        { question: `Can the planner use my Heathrow arrival flight for travel to ${city.city}?`, answer: "Yes. Select the arriving flight or enter its arrival time manually. The planner uses it when preparing the onward comparison after leaving the terminal." },
      ],
    },
  ];
}

const ROUTES = [...BASE_ROUTES, ...M1_CORRIDOR_CITIES.flatMap(corridorRoutePages)].map((page) => ({
  ...page,
  type: "ajp-route-planner",
  canonicalUrl: `${SITE_URL}${page.path}`,
  robots: "index,follow",
  lockedDirection: page.direction,
  trackingSource: "ajp_route_planner",
  sitemap: { changefreq: "weekly", priority: "0.85" },
}));

const ajpRoutePlannerPages = Object.freeze(ROUTES);

function getAjpRoutePlannerPage(key) {
  return ajpRoutePlannerPages.find((page) => page.key === key) || null;
}

export { ajpRoutePlannerPages, getAjpRoutePlannerPage };
